export interface DocumentationCategory {
  _id: string;
  title: string;
  slug: { current: string };
  icon?: string;
  description?: string;
  order?: number;
  articles?: DocumentationArticle[];
}

export interface DocumentationArticle {
  _id: string;
  title: string;
  slug: { current: string };
  description?: string;
  content: any[];
  featuredImage?: any;
  featuredVideo?: string;
  icon?: string;
  category?: DocumentationCategory;
  relatedArticles?: DocumentationArticle[];
  popular?: boolean;
  _updatedAt: string;
}

const CATEGORIES_QUERY = groq`
  *[_type == "documentationCategory"] | order(order asc, title asc) {
    _id,
    title,
    slug,
    icon,
    description,
    order,
    "articles": *[_type == "documentationArticle" && references(^._id)] | order(title asc) {
      _id,
      title,
      slug,
      description,
      popular
    }
  }
`;

const ARTICLE_QUERY = groq`
  *[_type == "documentationArticle" && slug.current == $slug][0] {
    _id,
    title,
    slug,
    description,
    content,
    featuredImage,
    icon,
    featuredVideo,
    popular,
    _updatedAt,
    category->{
      _id,
      title,
      slug,
      icon,
      description
    },
    "relatedArticles": relatedArticles[]->{
      _id,
      title,
      slug,
      description,
      category->{
        title,
        slug,
        icon
      }
    }
  }
`;

const SEARCH_QUERY = groq`
  *[_type == "documentationArticle" && (
    title match $query || 
    description match $query || 
    content[].children[].text match $query
  )] | order(_updatedAt desc) {
    _id,
    title,
    slug,
    description,
    icon,
    category->{
      title,
      slug,
      icon
    }
  }
`;

const POPULAR_TOPICS_QUERY = groq`
  *[_type == "documentationArticle" && popular == true] | order(_updatedAt desc) [0...6] {
    _id,
    title,
    slug,
    description,
    icon,
    category->{
      title,
      slug,
      icon
    }
  }
`;

const LATEST_ARTICLES_QUERY = groq`
  *[_type == "documentationArticle"] | order(_updatedAt desc) [0...2] {
    _id,
    title,
    slug,
    description,
    featuredImage,
    _updatedAt,
    icon,
    category->{
      title,
      slug,
      icon
    }
  }
`;

export const useDocumentationCategories = () => {
  return useSanityQuery<DocumentationCategory[]>(CATEGORIES_QUERY);
};

export const useDocumentationArticle = (slug: string | string[]) => {
  // Ensure slug is a string (handle array case from route params)
  const slugValue = Array.isArray(slug) ? slug[0] : slug;
  return useSanityQuery<DocumentationArticle>(ARTICLE_QUERY, { slug: slugValue });
};

export const useDocumentationSearch = (query: string) => {
  if (!query || query.trim().length === 0) {
    return ref({ data: [] });
  }

  const searchQuery = `*${query}*`;
  return useSanityQuery<DocumentationArticle[]>(SEARCH_QUERY, { query: searchQuery });
};

export const usePopularTopics = () => {
  return useSanityQuery<DocumentationArticle[]>(POPULAR_TOPICS_QUERY);
};

export const useLatestArticles = () => {
  return useSanityQuery<DocumentationArticle[]>(LATEST_ARTICLES_QUERY);
};
